import { slippageReductionStrategies } from '../data';

const groupBreaks = [
  { at: 0, label: 'Zero cost — do this today' },
  { at: 3, label: 'Requires planning or habit change' },
  { at: 5, label: 'Costs money or significant effort' },
];

export default function ReduceSlippageSection() {
  return (
    <section className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Editorial header */}
        <div className="max-w-[680px] mb-10 md:mb-14">
          <h2 className="text-[28px] md:text-[38px] font-[750] leading-[1.12] text-foreground-950 tracking-tight mb-4">
            7 ways to reduce slippage — grouped by what they cost you
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            Slippage is not a tax you have to pay. Some fixes are free and instant. Others cost
            money or effort. Start at the top and work your way down.
          </p>
        </div>

        {/* ── Strategy list ── */}
        <div className="border-t-2 border-foreground-950">
          {slippageReductionStrategies.map((s, idx) => {
            const groupBreak = groupBreaks.find((g) => g.at === idx);

            return (
              <div key={s.title}>
                {/* Group header — only at break points */}
                {groupBreak && (
                  <div className="pt-10 pb-2 first:pt-0">
                    <span className="text-[11px] font-bold text-foreground-500 uppercase tracking-[0.14em]">
                      {groupBreak.label}
                    </span>
                  </div>
                )}

                {/* Strategy row */}
                <div className="py-7 border-t border-background-200">
                  <div className="flex gap-5 md:gap-7">
                    {/* Number */}
                    <span className="text-[14px] font-[650] text-foreground-400 tabular-nums flex-shrink-0 leading-[1.65] mt-0.5">
                      {String(idx + 1).padStart(2, '0')}
                    </span>

                    {/* Content */}
                    <div className="min-w-0 max-w-[680px]">
                      {/* Title */}
                      <h3 className="text-[17px] font-[700] text-foreground-950 leading-[1.4] mb-2">
                        {s.title}
                      </h3>

                      {/* Insight */}
                      <p className="text-[14px] leading-[1.7] text-foreground-600 mb-3">
                        {s.insight}
                      </p>

                      {/* Impact + Cost as inline metadata */}
                      <div className="flex flex-wrap gap-x-8 gap-y-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-[0.08em]">
                            Impact
                          </span>
                          <span className="text-[13px] font-[650] text-foreground-800">
                            {s.impact}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-[0.08em]">
                            Cost
                          </span>
                          <span className="text-[13px] font-[650] text-foreground-800">
                            {s.cost}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom line */}
        <div className="mt-12 pt-6 border-t-2 border-foreground-950 flex items-start gap-4 max-w-[740px]">
          <span className="text-[14px] font-bold text-foreground-400 leading-relaxed whitespace-nowrap">Bottom line</span>
          <p className="text-[14px] leading-[1.7] text-foreground-600">
            You cannot eliminate slippage entirely — it is a feature of how markets work, not a bug.
            But by combining the right order type, a transparent broker, and sensible session timing,
            most retail traders can keep slippage below{' '}
            <strong className="font-semibold text-foreground-900">0.5 pips per trade</strong>. That
            turns slippage from a meaningful cost into a rounding error. The traders who lose serious
            money to slippage are almost always those who ignore it entirely.
          </p>
        </div>
      </div>
    </section>
  );
}